// src/components/agents/AgentToolsPanel.tsx
//
// Aba Tools do modal de agente (Reforma UX §6.6, AGT-05) — duas listas
// (permitidas × não permitidas); clique numa permitida remove, numa não
// permitida libera — sempre com modal de confirmação. Endpoints:
//   GET  /v1/admin/agent-tools?agent_id=…  (permitidas)
//   GET  /v1/admin/tools                   (universo de tools)
//   PATCH /v1/admin/agent-tools { agent_id, tool_slug, enabled }
//
// Endpoints admin: pra usuário sem admin o GET pode falhar — degrade com
// mensagem, sem quebrar o modal.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Check, Plus, Wrench, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AgentToolsPanelProps {
  /** UUID do agent (agents.id) — chave do N:N agent_tools. */
  agentId: string;
  /** Fallback inicial vindo do detail (config.allowed_tools). */
  initialAllowed?: string[];
  onError?: (message: string) => void;
}

interface PendingChange {
  toolSlug: string;
  /** true = liberar (não permitida → permitida); false = remover. */
  enable: boolean;
}

export function AgentToolsPanel({ agentId, initialAllowed, onError }: AgentToolsPanelProps) {
  const [allowed, setAllowed] = useState<string[]>(initialAllowed ?? []);
  const [pending, setPending] = useState<PendingChange | null>(null);

  // AGT-08: react-query com staleTime — o catálogo de tools muda raríssimo e
  // era refetchado a cada abertura da aba (2 GETs admin ~0.5-0.7s). Catálogo
  // compartilhado entre todos os agentes (5min); permitidas por agente (60s).
  // catch(null) preserva o degrade pra usuário sem admin.
  const { data: agentToolsData, isLoading: loadingAgentTools } = useQuery({
    queryKey: ['admin-agent-tools', agentId],
    queryFn: () => api.listAdminAgentTools({ agent_id: agentId }).catch(() => null),
    staleTime: 60_000,
  });
  const { data: catalogData, isLoading: loadingCatalog } = useQuery({
    queryKey: ['admin-tools-catalog'],
    queryFn: () => api.listAdminTools().catch(() => null),
    staleTime: 5 * 60_000,
  });
  const loading = loadingAgentTools || loadingCatalog;
  const allTools = (catalogData ?? []).filter((t) => t.is_active).map((t) => t.slug);
  const loadError =
    !loadingCatalog && catalogData === null
      ? 'Não foi possível listar o catálogo de tools (endpoint admin). Mostrando só as permitidas.'
      : '';

  useEffect(() => {
    if (!agentToolsData) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) {
        setAllowed(agentToolsData.filter((t) => t.enabled).map((t) => t.tool_slug));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [agentToolsData]);

  const denied = allTools.filter((slug) => !allowed.includes(slug)).sort();
  const allowedSorted = [...allowed].sort();

  const confirmChange = async () => {
    if (!pending) return;
    try {
      await api.patchAdminAgentTool(agentId, pending.toolSlug, pending.enable);
      setAllowed((prev) =>
        pending.enable
          ? [...prev, pending.toolSlug]
          : prev.filter((s) => s !== pending.toolSlug),
      );
    } catch (err) {
      onError?.(
        err instanceof Error
          ? `Não foi possível atualizar a tool: ${err.message}`
          : 'Não foi possível atualizar a tool.',
      );
      throw err; // mantém o modal aberto com o erro inline
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-raios-muted">
        Clique numa tool <strong>permitida</strong> para remover, ou numa{' '}
        <strong>não permitida</strong> para liberar — abre uma confirmação.
      </p>
      {loadError && <p className="text-xs text-raios-warning-text">{loadError}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h5 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-raios-success-text mb-2">
            <Check className="w-3.5 h-3.5" />
            Permitidas ({allowedSorted.length})
          </h5>
          <div className="flex flex-col gap-1.5">
            {allowedSorted.length === 0 && (
              <p className="text-xs text-raios-muted italic">Nenhuma tool permitida.</p>
            )}
            {allowedSorted.map((slug) => (
              <ToolChip
                key={slug}
                slug={slug}
                kind="allow"
                onClick={() => setPending({ toolSlug: slug, enable: false })}
              />
            ))}
          </div>
        </div>
        <div>
          <h5 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-raios-muted mb-2">
            <X className="w-3.5 h-3.5" />
            Não permitidas ({denied.length})
          </h5>
          <div className="flex flex-col gap-1.5">
            {denied.length === 0 && (
              <p className="text-xs text-raios-muted italic">
                {allTools.length === 0
                  ? 'Catálogo de tools indisponível.'
                  : 'Todas as tools estão liberadas.'}
              </p>
            )}
            {denied.map((slug) => (
              <ToolChip
                key={slug}
                slug={slug}
                kind="deny"
                onClick={() => setPending({ toolSlug: slug, enable: true })}
              />
            ))}
          </div>
        </div>
      </div>

      {pending && (
        <ConfirmActionDialog
          open={!!pending}
          onOpenChange={(o) => {
            if (!o) setPending(null);
          }}
          title={pending.enable ? 'Liberar tool pro agente?' : 'Remover tool do agente?'}
          description={
            pending.enable
              ? 'O agente passa a poder chamar esta tool nas próximas execuções.'
              : 'O agente deixa de poder chamar esta tool nas próximas execuções.'
          }
          confirmLabel={pending.enable ? 'Liberar' : 'Remover'}
          confirmVariant={pending.enable ? 'brand' : 'destructive'}
          onConfirm={confirmChange}
        >
          <p className="text-sm text-raios-text-secondary">
            Tool: <span className="font-mono text-raios-text">{pending.toolSlug}</span>
          </p>
        </ConfirmActionDialog>
      )}
    </div>
  );
}

function ToolChip({
  slug,
  kind,
  onClick,
}: {
  slug: string;
  kind: 'allow' | 'deny';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={kind === 'allow' ? `Remover ${slug}` : `Liberar ${slug}`}
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-mono text-left',
        'transition-colors cursor-pointer',
        kind === 'allow'
          ? 'border-raios-success/40 bg-raios-success/5 text-raios-text hover:bg-raios-danger/10 hover:border-raios-danger/40'
          : 'border-raios-border text-raios-text-secondary hover:bg-raios-success/10 hover:border-raios-success/40',
      )}
    >
      {kind === 'allow' ? (
        <Wrench className="w-3 h-3 shrink-0 text-raios-success-text" />
      ) : (
        <Plus className="w-3 h-3 shrink-0 text-raios-muted" />
      )}
      {slug}
    </button>
  );
}
